<script setup lang="ts">
/**
 * Form 45 — Consent to Act as Director & Statement of Non-Disqualification
 * (Companies Act 1967, section 173C(a)). Aura 개정판(2025) 원본 기준.
 *
 * · 조건부(원문 * "Delete as appropriate")는 클릭 토글 → fill 'yes'/'no' → docx [[if …]]:
 *     (iv) 1993년 이전 조항  = has_pre_1993_history
 *     (ba) 노미니 디렉터      = is_nominee_director
 *     (bb) Employment Pass    = is_employment_pass_holder
 *     (c)(i)/(ii) 읽음/통역    = needs_interpreter (on=통역, 통역정보 노출)
 * · 조항 문구는 Aura ver-2026 통일본(생성 docx)과 같은 원문 전문 — (v)·(vi)·(b) 포함, 주소 각주 i/ii.
 * · 이사 particulars(Name/Residential·Contact Address/ID/Nationality/Email/Mobile) + 서명 + Witness 블록.
 * · 패키지에서 DocScope(f45_{id}/) 로 디렉터별 반복.
 */
import { computed, inject } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import SgPreSigners from './SgPreSigners.vue';
import { useCddFill, DOC_SCOPE } from './cdd/useCddFill';
import './allotDoc.css';

const fill = useCddFill();
// DocScope(f45_{id}/) 안에서 조건부 토글 키도 이사별로 분리 — CddVarField 와 동일하게 scope + key.
const scope = inject(DOC_SCOPE, '');
const skey = (k: string) => scope + k;
// 이사별 서명 [[Signers]] varKey — 부수(f45_{id}/)마다 독립.
const signerKey = computed(() => `${scope}director_Signers`);

function isOn(key: string): boolean {
  const v = String(fill.values[skey(key)] ?? '').trim().toLowerCase();
  return v === 'yes' || v === 'true' || v === 'y';
}
function toggle(key: string) {
  fill.setValue(skey(key), isOn(key) ? 'no' : 'yes');
}
function setFlag(key: string, val: string) {
  fill.setValue(skey(key), val);
}
const needsInterpreter = computed(() => isOn('needs_interpreter'));
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head" style="text-align:center">
      <div class="doc-meta">THE COMPANIES ACT 1967</div>
      <div class="doc-meta">SECTION 173C(a)</div>
      <div class="doc-lbl-title" style="margin:4px 0">
        CONSENT TO ACT AS DIRECTOR AND STATEMENT OF NON DISQUALIFICATION TO ACT AS DIRECTOR
      </div>
      <div class="doc-sec-title" style="letter-spacing:.2em">FORM 45</div>
    </div>

    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Name of Company</td>
          <td><CddVarField field-key="company_name" label="Company Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Company No</td>
          <td><CddVarField field-key="company_registration_no" label="Company Registration No." /></td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p" style="margin-top:12px">
      I, the undermentioned person, hereby consent to act as a director of the abovenamed company with effect from
      <CddVarField field-key="appointment_effective_date" label="Consent Effective Date" /> (date) and declare that:
    </p>

    <p class="doc-p">(a)&nbsp;&nbsp;I am not disqualified from acting as a director, in that:</p>
    <p class="doc-p indent">(i)&nbsp;&nbsp;I am not below 18 years of age and that I am otherwise of full legal capacity.</p>
    <p class="doc-p indent">
      (ii)&nbsp;&nbsp;Within a period of 3 years preceding the date of this statement I have not had any disqualification
      order made by the General Division of the High Court of Singapore against me under section 149A(1) of the Companies
      Act 1967 (&ldquo;the Act&rdquo;).
    </p>
    <p class="doc-p indent">
      (iii)&nbsp;&nbsp;Within a period of 5 years preceding the date of this statement I have not had any disqualification
      order made by the General Division of the High Court of Singapore against me under section 149(1) or 154(2) of the
      Act.
    </p>

    <!-- (iv) 1993년 이전 조항 — 조건부 토글 -->
    <p
      class="doc-p indent cond-row doc-toggle"
      :class="{ off: !isOn('has_pre_1993_history') }"
      title="Include the pre-12 Nov 1993 conviction declaration (iv)? Click to toggle."
      @click="toggle('has_pre_1993_history')"
    >
      <span class="cond-mark">{{ isOn('has_pre_1993_history') ? '☒' : '☐' }}</span>
      <span class="cond-txt">
        *(iv)&nbsp;&nbsp;That within a period of 5 years preceding 12th November 1993 I have not been convicted whether
        within or without Singapore, of any offence &mdash; (A) in connection with the promotion, formation or management of a
        corporation; (B) involving fraud or dishonesty punishable on conviction with imprisonment for 3 months or more; or
        (C) under section 157 (failure to act honestly and diligently as a director or making improper use of company
        information for gain) of the Act; and
      </span>
    </p>

    <!-- (v)·(vi)·(b) — 생성 문서(Aura ver-2026 통일본)와 같은 원문 전문 -->
    <p class="doc-p indent">
      *(v)&nbsp;&nbsp;That &mdash; (A) within a period of 5 years preceding the date of this statement I have not been
      convicted, in Singapore or elsewhere, of any offence involving fraud or dishonesty punishable on conviction with
      imprisonment for 3 months or more; and (B) within a period of 5 years preceding the date of this statement I have not
      been a director of 3 or more companies which names had been struck off the register under section 344(4) read with
      section 344(1) of the Act; and I was not, at the time the name of each of those companies was struck off the
      register, a director of that company.
    </p>
    <p class="doc-p indent">
      (vi)&nbsp;&nbsp;That &mdash; (A) I have not been convicted of 3 or more offences under the Act in relation to the
      requirements on the filing of returns, accounts or other documents with the Registrar of Companies and have not had 3
      or more orders of the General Division of the High Court of Singapore made against me under section 13 or 399 of the
      Act in relation to such requirements; (B) the last of any such conviction did not take place or the last of any such
      order was not made during the period of 5 years preceding the date of this statement; (C) I am not an undischarged
      bankrupt under section 148(1) of the Act; and (D) There is currently no debarment order under section 155B of the Act
      in force against me.
    </p>
    <p class="doc-p indent">
      (vii)&nbsp;&nbsp;By virtue of the foregoing I am not disqualified from acting as a director of the abovenamed company.
    </p>

    <p class="doc-p">
      (b)&nbsp;&nbsp;I am aware of and undertake to abide by my duties, responsibilities and liabilities specified in the
      Act as well as under the common law where applicable, including the following key administrative and substantive
      duties, that is, to:
    </p>
    <p class="doc-p indent">(i)&nbsp;&nbsp;discharge my responsibilities in the company;</p>
    <p class="doc-p indent">(ii)&nbsp;&nbsp;ensure that I have a reasonable degree of skill and knowledge to handle the affairs of the company;</p>
    <p class="doc-p indent">
      (iii)&nbsp;&nbsp;act honestly and be reasonably diligent in discharging my duties and act in the interest of the company
      without putting myself in a position of conflict of interest;
    </p>
    <p class="doc-p indent">
      (iv)&nbsp;&nbsp;employ the powers and assets that I am entrusted with for the proper purposes of the company and not for
      any collateral purpose;
    </p>
    <p class="doc-p indent">
      (v)&nbsp;&nbsp;ensure that the company and I comply with all the requirements and obligations under the Act including
      those in respect of meetings, requisitions, resolutions, accounts, reports, statements, records and other documents
      on the company, filing and notices and any other prerequisites; and
    </p>
    <p class="doc-p indent">
      (vi)&nbsp;&nbsp;account to the shareholders for my conduct of the affairs of the company and make such disclosures that
      are incumbent upon me under the Act.
    </p>

    <!-- (ba) 노미니 디렉터 — 조건부 토글 -->
    <p
      class="doc-p cond-row doc-toggle"
      :class="{ off: !isOn('is_nominee_director') }"
      title="Nominee director declaration (ba)? Click to toggle."
      @click="toggle('is_nominee_director')"
    >
      <span class="cond-mark">{{ isOn('is_nominee_director') ? '☒' : '☐' }}</span>
      <span class="cond-txt">
        *(ba)&nbsp;&nbsp;I am acting as a nominee director of the abovenamed company by way of business, and I am a
        registered corporate service provider providing the corporate service of acting, or arranging for another person
        to act, as a director of a corporation; or my acting is arranged by a registered corporate service provider for
        that corporate service.
      </span>
    </p>

    <!-- (bb) Employment Pass — 조건부 토글 -->
    <p
      class="doc-p cond-row doc-toggle"
      :class="{ off: !isOn('is_employment_pass_holder') }"
      title="Employment Pass holder declaration (bb)? Click to toggle."
      @click="toggle('is_employment_pass_holder')"
    >
      <span class="cond-mark">{{ isOn('is_employment_pass_holder') ? '☒' : '☐' }}</span>
      <span class="cond-txt">
        *(bb)&nbsp;&nbsp;I am an Employment Pass holder and permission has been obtained from the Ministry of Manpower for
        me to take on this directorship.
      </span>
    </p>

    <!-- (c) 읽음 vs 통역 — needs_interpreter 토글 -->
    <p class="doc-p">(c)&nbsp;&nbsp;That &mdash;</p>
    <p
      class="doc-p indent cond-row doc-toggle"
      :class="{ off: needsInterpreter }"
      title="I have read and understood (c)(i)"
      @click="setFlag('needs_interpreter', 'no')"
    >
      <span class="cond-mark">{{ !needsInterpreter ? '☒' : '☐' }}</span>
      <span class="cond-txt">*(i)&nbsp;&nbsp;I have read and understood the above statements; or</span>
      <!-- 생성 문서에는 고른 쪽 한 줄만 남는다 ([[if needs_interpreter]]…[[else]]…[[endif]]) -->
    </p>
    <p
      class="doc-p indent cond-row doc-toggle"
      :class="{ off: !needsInterpreter }"
      title="The statements were interpreted to me (c)(ii)"
      @click="setFlag('needs_interpreter', 'yes')"
    >
      <span class="cond-mark">{{ needsInterpreter ? '☒' : '☐' }}</span>
      <span class="cond-txt">*(ii)&nbsp;&nbsp;the above statements were interpreted to me in</span>
    </p>

    <template v-if="needsInterpreter">
      <p class="doc-p indent">
        <CddVarField field-key="interpreter_language" label="Language / Dialect" /> (state language/dialect)
      </p>
      <p class="doc-p indent">
        by <CddVarField field-key="interpreter_name" label="Interpreter Name" /> (state name)
      </p>
      <p class="doc-p indent">
        NRIC NO: <CddVarField field-key="interpreter_nric" label="Interpreter NRIC" />
      </p>
    </template>

    <p class="doc-p">
      before I executed this form and I confirm that the statements are true. I am also aware that I can be prosecuted in
      Court if I willfully give any information on this form which is false
    </p>

    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Name</td>
          <td><CddVarField field-key="director_name" label="Director Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Residential Address<sup>i</sup></td>
          <td><CddVarField field-key="director_residential_address" label="Director Residential Address" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Contact Address<sup>ii</sup></td>
          <td><CddVarField field-key="director_contact_address" label="Director Contact Address" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">*NRIC/FIN/Passport No.</td>
          <td><CddVarField field-key="director_identification_no" label="Director NRIC / FIN / Passport No." /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Nationality</td>
          <td><CddVarField field-key="director_nationality" label="Director Nationality" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Email address</td>
          <td><CddVarField field-key="director_email" label="Director Email" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Mobile No.</td>
          <td><CddVarField field-key="director_mobile_no" label="Director Mobile No." /></td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p" style="margin-top:12px">Dated this: <CddVarField field-key="dated_on" label="Dated On" /></p>

    <!-- 서명 블록 [[Signers]] — 이 부수의 이사 본인(수동 추가/편집·T-Sign) -->
    <div class="sig-block">
      <div class="sig-lbl">Signature:</div>
      <SgPreSigners :var-key="signerKey" board-label="" />
      <div class="doc-p" style="margin-top:4px">(Signature)</div>
    </div>

    <!-- Witness 란 — 이름·ID·직업·일자 (원문에 별도 "Witness" 제목 없음) -->
    <table class="cdd-tbl" style="margin-top:12px">
      <tbody>
        <tr>
          <td class="cdd-lcell">Witness Name</td>
          <td><CddVarField field-key="witness_name" label="Witness Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">*NRIC / FIN / Passport No.</td>
          <td><CddVarField field-key="witness_identification_no" label="Witness Identification No." /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Occupation</td>
          <td><CddVarField field-key="witness_occupation" label="Witness Occupation" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Date</td>
          <td><CddVarField field-key="witness_date" label="Witness Date" /></td>
        </tr>
      </tbody>
    </table>

    <div class="doc-attn">
      <p>*Delete as appropriate.</p>
      <p><sup>i</sup> Residential Address: This is the individual's actual place of residence and is not made public in ACRA's records if a Contact Address is provided.</p>
      <p><sup>ii</sup> Contact Address: This is a publicly displayed address chosen by the officer to protect their privacy. It can be a business address, a corporate service provider's address, or any other valid physical address within the same jurisdiction</p>
    </div>
  </div>
</template>

<style scoped>
.indent { padding-left: 20px; }
.cond-row { display: flex; gap: 8px; align-items: flex-start; }
.cond-row.off { background: transparent; box-shadow: none; opacity: 0.55; }
.cond-mark { font-size: 15px; line-height: 1.3; flex: 0 0 auto; }
.cond-txt { flex: 1; }
.doc-attn p { margin: 0; }
.sig-block { margin-top: 18px; }
.sig-lbl { font-weight: 700; margin-bottom: 6px; }
</style>
